import { 
  BusinessIdeaGenerationParams, 
  BusinessIdea 
} from './businessIdeaTypes';
import { ENV } from '../config/env';
import { supabase } from '../config/supabaseClient';

// Use centralized ENV configuration for API URL
const API_BASE_URL = `${ENV.API_URL}/api`;

// Get the authentication token from Supabase
const getToken = async (): Promise<string> => {
  try {
    const { data } = await supabase.auth.getSession();
    return data.session?.access_token || '';
  } catch (e) {
    console.error('Error getting Supabase auth token:', e);
    return '';
  }
};

// Subtitles/Whisper integration
// Requests backend to generate subtitles (WebVTT) for a given videoId.
// The backend should either return a saved subtitlePath or VTT text to be saved client-side.
export const generateSubtitles = async (
  videoId: string
): Promise<{ vtt?: string; subtitlePath?: string }> => {
  if (!videoId) {
    throw new Error('videoId is required to generate subtitles');
  }

  const url = `${API_BASE_URL}/videos/${encodeURIComponent(videoId)}/subtitles/generate`;
  try {
    const token = await getToken();
    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      },
      body: JSON.stringify({ model: 'whisper-1' })
    });

    if (!response.ok) {
      const text = await response.text();
      let message = text;
      try { message = JSON.parse(text)?.message || text; } catch {}
      throw new Error(`Subtitle generation failed: ${response.status} ${response.statusText}. ${message}`);
    }

    const data = await response.json();
    // Expecting { subtitlePath?: string, vtt?: string }
    if (!data.subtitlePath && !data.vtt) {
      throw new Error('Backend did not return subtitlePath or vtt');
    }
    return data;
  } catch (err) {
    console.error('Error generating subtitles:', err);
    throw err instanceof Error ? err : new Error('Unknown error generating subtitles');
  }
};

// Utility function to safely get environment variables
// eslint-disable-next-line @typescript-eslint/no-unused-vars
const getEnvVar = (key: string, fallback = ''): string => {
  // Check if import.meta exists and has env
  try {
    if (typeof import.meta !== 'undefined' && import.meta.env) {
      // Try both Vite-specific and standard key formats
      const viteKey = `VITE_${key}`;
      const standardKey = key;

      console.log(`Checking env vars:`, {
        viteKey: import.meta.env[viteKey] ? 'defined' : 'undefined',
        standardKey: import.meta.env[standardKey] ? 'defined' : 'undefined'
      });

      return import.meta.env[viteKey] || 
             import.meta.env[standardKey] || 
             fallback;
    }
  } catch {
    // Silently fall through to fallback
  }
  
  console.error('No environment variable sources available');
  return fallback;
};

export const generateBusinessIdeas = async (params: BusinessIdeaGenerationParams): Promise<BusinessIdea[]> => {
  try {
    console.log('Generating Business Ideas - Detailed Input:');
    console.log('Skills:', JSON.stringify(params.skills, null, 2));
    console.log('Time Commitment:', params.timeCommitment);
    console.log('Work Environment:', params.workEnvironment);
    console.log('Market Scope:', params.marketScope);

    console.log('Full API URL:', `${API_BASE_URL}/business-ideas/generate`);
    console.log('Request params:', JSON.stringify(params, null, 2));

    // Validate input before sending
    if (!params.skills || params.skills.length === 0) {
      console.error('No skills provided');
      throw new Error('Skills are required for idea generation');
    }
    if (!params.timeCommitment) {
      console.error('No time commitment provided');
      throw new Error('Time commitment is required for idea generation');
    }
    if (!params.workEnvironment) {
      console.error('No work environment provided');
      throw new Error('Work environment is required for idea generation');
    }
    if (!params.marketScope) {
      console.error('No market scope provided');
      throw new Error('Market scope is required for idea generation');
    }

    try {
      // Get the user data to extract userId
      const { data: userData } = await supabase.auth.getUser();
      const userId = userData?.user?.id;
      
      if (!userId) {
        console.error('No authenticated user found');
        throw new Error('User authentication required for idea generation');
      }
      
      console.log('Including userId in request:', userId);
      
      const response = await fetch(`${API_BASE_URL}/business-ideas/generate`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${await getToken()}`
        },
        body: JSON.stringify({
          ...params,
          userId: userId // Include userId in the request body
        })
      });

      console.log('Response status:', response.status);
      console.log('Response headers:', Object.fromEntries(response.headers.entries()));

      // Detailed error handling for non-200 responses
      if (!response.ok) {
        const errorText = await response.text();
        console.error('Error response text:', errorText);

        // Parse error response if possible
        let errorDetails: { message?: string } = {};
        try {
          errorDetails = JSON.parse(errorText);
        } catch {
          errorDetails = { message: errorText };
        }

        throw new Error(`Network error: ${response.status} ${response.statusText}. ${errorDetails.message || ''}`);
      }

      const responseData = await response.json();
      console.log('Response data:', responseData);

      return responseData;
    } catch (fetchError: unknown) {
      // Type-safe error logging
      console.error('Fetch error details:', {
        name: fetchError instanceof Error ? fetchError.name : 'Unknown Error',
        message: fetchError instanceof Error ? fetchError.message : 'Unknown error occurred',
        stack: fetchError instanceof Error ? fetchError.stack : 'No stack trace available'
      });

      // More specific error handling for network issues
      if (fetchError instanceof TypeError) {
        throw new Error(`Network connection error: ${fetchError instanceof Error ? fetchError.message : 'Unknown'}. 
          Please check:
          1. Backend server is running
          2. CORS settings are correct
          3. Network connectivity`);
      }

      // Rethrow any other errors
      throw fetchError instanceof Error 
        ? fetchError 
        : new Error('An unknown error occurred during fetch');
    }
  } catch (error: unknown) {
    // Type-safe outer error handling
    const errorMessage = error instanceof Error 
      ? error.message 
      : 'Unknown error generating business ideas';

    console.error('Detailed error generating business ideas:', {
      message: errorMessage,
      params: JSON.stringify(params)
    });

    throw new Error(errorMessage);
  }
};

export const generateMarketResearch = async (params: MarketResearchParams): Promise<MarketResearchResponse> => {
  try {
    console.log('Full API URL:', `${API_BASE_URL}/market-research/generate`);
    console.log('Request params:', JSON.stringify(params, null, 2));

    try {
      const response = await fetch(`${API_BASE_URL}/market-research/generate`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${await getToken()}`
        },
        body: JSON.stringify(params)
      });

      console.log('Response status:', response.status);
      console.log('Response headers:', Object.fromEntries(response.headers.entries()));

      // Detailed error handling for non-200 responses
      if (!response.ok) {
        const errorText = await response.text();
        console.error('Error response text:', errorText);

        // Parse error response if possible
        let errorDetails: { message?: string } = {};
        try {
          errorDetails = JSON.parse(errorText);
        } catch {
          errorDetails = { message: errorText };
        }

        throw new Error(`Network error: ${response.status} ${response.statusText}. ${errorDetails.message || ''}`);
      }

      const responseData = await response.json();
      console.log('Response data:', responseData);

      return responseData;
    } catch (fetchError: unknown) {
      // Type-safe error logging
      console.error('Fetch error details:', {
        name: fetchError instanceof Error ? fetchError.name : 'Unknown Error',
        message: fetchError instanceof Error ? fetchError.message : 'Unknown error occurred',
        stack: fetchError instanceof Error ? fetchError.stack : 'No stack trace available'
      });

      // More specific error handling for network issues
      if (fetchError instanceof TypeError) {
        throw new Error(`Network connection error: ${fetchError instanceof Error ? fetchError.message : 'Unknown'}. 
          Please check:
          1. Backend server is running
          2. CORS settings are correct
          3. Network connectivity`);
      }

      // Rethrow any other errors
      throw fetchError instanceof Error 
        ? fetchError 
        : new Error('An unknown error occurred during market research generation');
    }
  } catch (error: unknown) {
    // Type-safe outer error handling
    const errorMessage = error instanceof Error 
      ? error.message 
      : 'Unknown error generating market research';

    console.error('Detailed error generating market research:', {
      message: errorMessage,
      params: JSON.stringify(params)
    });

    throw new Error(errorMessage);
  }
};

export const generateBusinessPlan = async (params: BusinessPlanParams): Promise<BusinessPlan> => {
  try {
    console.log('Full API URL:', `${API_BASE_URL}/business-plan/generate`);
    console.log('Request params:', JSON.stringify(params, null, 2));

    try {
      const response = await fetch(`${API_BASE_URL}/business-plan/generate`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${await getToken()}`
        },
        body: JSON.stringify(params)
      });

      console.log('Response status:', response.status);
      console.log('Response headers:', Object.fromEntries(response.headers.entries()));

      // Detailed error handling for non-200 responses
      if (!response.ok) {
        const errorText = await response.text();
        console.error('Error response text:', errorText);

        // Parse error response if possible
        let errorDetails: { message?: string } = {};
        try {
          errorDetails = JSON.parse(errorText);
        } catch {
          errorDetails = { message: errorText };
        }

        throw new Error(`Network error: ${response.status} ${response.statusText}. ${errorDetails.message || ''}`);
      }

      const responseData = await response.json();
      console.log('Response data:', responseData);

      return responseData;
    } catch (fetchError: unknown) {
      // Type-safe error logging
      console.error('Fetch error details:', {
        name: fetchError instanceof Error ? fetchError.name : 'Unknown Error',
        message: fetchError instanceof Error ? fetchError.message : 'Unknown error occurred',
        stack: fetchError instanceof Error ? fetchError.stack : 'No stack trace available'
      });

      // More specific error handling for network issues
      if (fetchError instanceof TypeError) {
        throw new Error(`Network connection error: ${fetchError instanceof Error ? fetchError.message : 'Unknown'}. 
          Please check:
          1. Backend server is running
          2. CORS settings are correct
          3. Network connectivity`);
      }

      // Rethrow any other errors
      throw fetchError instanceof Error 
        ? fetchError 
        : new Error('An unknown error occurred during business plan generation');
    }
  } catch (error: unknown) {
    // Type-safe outer error handling
    const errorMessage = error instanceof Error 
      ? error.message 
      : 'Unknown error generating business plan';

    console.error('Detailed error generating business plan:', {
      message: errorMessage,
      params: JSON.stringify(params)
    });

    throw new Error(errorMessage);
  }
};

// eslint-disable-next-line @typescript-eslint/no-unused-vars
async function generateMarketingPlan(params: { 
  sections: string[];
  businessIdea: any;
  marketResearch: any;
}): Promise<Record<string, string>> {
  throw new Error('Marketing plan generation has been removed');
}

// Helper function to parse generated plan into structured sections
// eslint-disable-next-line @typescript-eslint/no-unused-vars
function parsePlanSections(rawText: string): BusinessPlan['sections'] {
  const sections = {
    executiveSummary: {
      title: 'Executive Summary',
      content: extractSection(rawText, 'Executive Summary') || 'No content found',
      keyInsights: []
    },
    companyDescription: {
      title: 'Company Description',
      content: extractSection(rawText, 'Company Description') || 'No content found',
      keyInsights: []
    },
    marketAnalysis: {
      title: 'Market Analysis',
      content: extractSection(rawText, 'Market Analysis') || 'No content found',
      keyInsights: []
    },
    organizationAndManagement: {
      title: 'Organization and Management',
      content: extractSection(rawText, 'Organization and Management') || 'No content found',
      keyInsights: []
    },
    productsServices: {
      title: 'Products/Services',
      content: extractSection(rawText, 'Products/Services') || 'No content found',
      keyInsights: []
    },
    marketingAndSalesStrategy: {
      title: 'Marketing and Sales Strategy',
      content: extractSection(rawText, 'Marketing and Sales Strategy') || 'No content found',
      keyInsights: []
    },
    financialProjections: {
      title: 'Financial Projections',
      content: extractSection(rawText, 'Financial Projections') || 'No content found',
      keyInsights: []
    },
    fundingRequest: {
      title: 'Funding Request',
      content: extractSection(rawText, 'Funding Request') || 'No content found',
      keyInsights: []
    },
    appendix: {
      title: 'Appendix',
      content: extractSection(rawText, 'Appendix') || 'No content found',
      keyInsights: []
    }
  };

  // Ensure each section has at least a placeholder content
  for (const key in sections) {
    if (!sections[key as keyof BusinessPlan['sections']].content.trim()) {
      sections[key as keyof BusinessPlan['sections']].content = 'No content found';
    }
  }

  return sections;
}

// Helper function to extract specific sections from raw text
function extractSection(text: string, sectionTitle: string): string {
  const sectionRegex = new RegExp(`${sectionTitle}:?\\n(.*?)(?=\\n\\n[A-Z]|$)`, 's');
  const match = text.match(sectionRegex);
  return match ? match[1].trim() : 'No content found';
}

export type { 
  BusinessIdeaGenerationParams,
  BusinessIdea
};

interface RawBreakdownItem {
  category: string;
  cost: number | string;
  justification: string;
}

interface RawMonthlyPricing {
  basePrice: number | string;
  additionalRevenueSources: number | string;
}

interface RawAnnualProjection {
  year1: number | string;
  year3: number | string;
  year5: number | string;
}

interface RawTimeBasedEarnings {
  hoursPerWeek: number | string;
  potentialHourlyRate: number | string;
  weeklyEarningsPotential: number | string;
  monthlyEarningsPotential: number | string;
  annualEarningsPotential: number | string;
}

interface RawMarketingStrategy {
  targetAudience: string[];
  marketingChannels: string[];
  customerAcquisitionCost: number | string;
  conversionStrategy: string;
}

// eslint-disable-next-line @typescript-eslint/no-unused-vars
interface RawBusinessIdea {
  title: string;
  description: string;
  potentialMarket: string;
  skillsUtilized?: string[];
  initialInvestment?: {
    totalCost: number | string;
    breakdownItems: RawBreakdownItem[];
  };
  revenueModel?: {
    primaryRevenueSources: string[];
    pricingStrategy: string;
    monthlyPricing: RawMonthlyPricing;
    annualProjection: RawAnnualProjection;
  };
  timeBasedEarnings?: RawTimeBasedEarnings;
  marketingStrategy?: RawMarketingStrategy;
  innovationHighlights?: string[];
  potentialChallenges?: string[];
}

interface MarketResearchParams {
  businessIdea: {
    id: string;
    title: string;
    description: string;
  };
  marketScope: 'local' | 'national' | 'global';
}

interface MarketResearchResponse {
  marketSize: {
    totalAddressableMarket: number;
    serviceableAddressableMarket: number;
    growthRate: number;
  };
  competitiveLandscape: {
    topCompetitors: Array<{
      name: string;
      marketShare: number;
    }>;
  };
  targetAudience: {
    demographics: string;
    painPoints: string[];
  };
  riskAndOpportunityAssessment: {
    swotAnalysis: {
      strengths: string[];
      weaknesses: string[];
      opportunities: string[];
      threats: string[];
    };
    marketEntryBarriers: {
      technicalBarriers: string[];
      financialBarriers: string[];
      regulatoryBarriers: string[];
      marketAdoptionChallenges: string[];
    };
    regulatoryLandscape: {
      potentialChallenges: string[];
      complianceRequirements: string[];
      industryRegulations: string[];
    };
    investmentAttractiveness: {
      overallScore: number;
      riskLevel: 'Low' | 'Medium' | 'High';
      potentialReturn: number;
      breakEvenTimeframe: number;
      recommendedInvestmentStrategy: string;
    };
  };
  comparativeAnalysis?: ComparativeAnalysis;
}

interface ComparativeAnalysis {
  similarBusinessIdeas: SimilarBusinessIdea[];
  historicalTrends: MarketTrend[];
  industryBenchmarks: IndustryBenchmark[];
  competitiveIntelligence: CompetitiveIntelligenceReport;
}

interface SimilarBusinessIdea {
  title: string;
  description: string;
  marketPotential: {
    totalAddressableMarket: number;
    marketShare: number;
    growthRate: number;
  };
  comparisonScore: number; // 0-100 similarity score
}

interface MarketTrend {
  year: number;
  marketSize: number;
  growthRate: number;
  keyDrivers: string[];
}

interface IndustryBenchmark {
  metric: string;
  currentBusinessValue: number;
  industryAverage: number;
  percentageDifference: number;
  interpretation: 'Below Average' | 'Average' | 'Above Average';
}

interface CompetitiveIntelligenceReport {
  topCompetitors: Array<{
    name: string;
    strengths: string[];
    weaknesses: string[];
    marketStrategy: string;
  }>;
  competitiveAdvantages: string[];
  potentialDisruptors: string[];
}

interface BusinessPlanParams {
  businessIdea: BusinessIdea;
  marketResearch?: MarketResearchResponse;
}

interface BusinessPlanSection {
  title: string;
  content: string;
  keyInsights?: string[];
}

interface BusinessPlan {
  id?: string;
  businessIdea: BusinessIdea;
  sections: {
    executiveSummary: BusinessPlanSection;
    companyDescription: BusinessPlanSection;
    marketAnalysis: BusinessPlanSection;
    organizationAndManagement: BusinessPlanSection;
    productsServices: BusinessPlanSection;
    marketingAndSalesStrategy: BusinessPlanSection;
    financialProjections: BusinessPlanSection;
    fundingRequest: BusinessPlanSection;
    appendix: BusinessPlanSection;
  };
  generatedAt: Date;
  lastUpdated: Date;
}

// eslint-disable-next-line @typescript-eslint/no-unused-vars
interface BusinessNameOption {
  name: string;
  tagline: string;
  reasoning: string;
}
